import { useMemo } from 'react';
import {
  Dices, Flame, Gift, Headphones, Home, Menu, PlayCircle,
  Rocket, Search, Send, Sparkles, Star, Target, Ticket, Trophy, Tv, User, Wallet,
} from 'lucide-react';
import { useSettings } from '../context/SettingsContext';
import { useHiddenCategories } from './gameCategories';

/**
 * Mobil alt menü — panelden yönetilen yuvalar.
 *
 * Bar beş yuvadır: dördü buradan gelir, ortadaki sabittir (girişsiz
 * ziyaretçide "Giriş Yap", üyede "Para Yatır") ve panelden değiştirilemez.
 * Ayar `settings.mobile_bottom_nav` içinde durur; kayıt boşsa ya da bozuksa
 * aşağıdaki varsayılana düşülür.
 */

// Panelde seçilebilen ikonlar. Anahtarlar backend'e metin olarak gider;
// admin/Settings.jsx aynı listeyi etiketleriyle birlikte gösterir.
export const BOTTOM_NAV_ICONS = {
  home: Home,
  trophy: Trophy,
  dices: Dices,
  'play-circle': PlayCircle,
  rocket: Rocket,
  gift: Gift,
  menu: Menu,
  search: Search,
  ticket: Ticket,
  target: Target,
  flame: Flame,
  sparkles: Sparkles,
  headphones: Headphones,
  user: User,
  wallet: Wallet,
  star: Star,
  // İletişim bağlantıları (Telegram kanalı, canlı yayın/TV) için.
  send: Send,
  tv: Tv,
};

export const BOTTOM_NAV_ICON_OPTIONS = [
  { value: 'home', label: 'Anasayfa' },
  { value: 'trophy', label: 'Spor' },
  { value: 'dices', label: 'Slot' },
  { value: 'play-circle', label: 'Canlı Casino' },
  { value: 'rocket', label: 'Hızlı Oyun' },
  { value: 'gift', label: 'Bonus' },
  { value: 'menu', label: 'Menü' },
  { value: 'search', label: 'Arama' },
  { value: 'ticket', label: 'Kupon' },
  { value: 'target', label: 'Çark' },
  { value: 'flame', label: 'Popüler' },
  { value: 'sparkles', label: 'Yeni' },
  { value: 'headphones', label: 'Destek' },
  { value: 'user', label: 'Hesap' },
  { value: 'wallet', label: 'Cüzdan' },
  { value: 'star', label: 'Favori' },
  { value: 'send', label: 'Telegram' },
  { value: 'tv', label: 'TV / Yayın' },
];

// Panel hiç dokunulmadığında gösterilen düzen.
export const DEFAULT_BOTTOM_NAV = [
  { label: 'Anasayfa', icon: 'home', type: 'link', url: '/' },
  { label: 'Slotlar', icon: 'dices', type: 'link', url: '/slotlar' },
  { label: 'Bonuslar', icon: 'gift', type: 'link', url: '/promosyonlar' },
  { label: 'Menü', icon: 'menu', type: 'menu', url: '' },
];

export const MAX_BOTTOM_NAV_ITEMS = 4;

// Kategoriye bağlı adresler: kategori panelden kapatıldığında ya da kataloğu
// boşaldığında yuva da düşer — yoksa oyuncu boş bir sayfaya gider.
// Üst menü de aynı listeyi kullanır (lib/topNav.js).
export const ROUTE_CATEGORIES = {
  '/slotlar': 'slot',
  '/canli-casino': 'live-casino',
  '/crash': 'crash',
};

export const pathOf = (url) => String(url || '').split(/[?#]/)[0].replace(/\/+$/, '') || '/';

// Object.prototype anahtarları ("constructor" gibi) ikon sayılmasın.
export const knownIcon = (name) => (
  Object.prototype.hasOwnProperty.call(BOTTOM_NAV_ICONS, String(name || ''))
);

/** Ayar kaydını çizilebilir yuvalara indirger; bozuk kayıtta varsayılana düşer. */
export const normalizeBottomNav = (raw) => {
  const items = (Array.isArray(raw) ? raw : [])
    .map((item) => ({
      label: String(item?.label || '').trim(),
      icon: knownIcon(item?.icon) ? item.icon : 'home',
      type: item?.type === 'menu' ? 'menu' : 'link',
      url: String(item?.url || '').trim(),
    }))
    .filter((item) => item.label && (item.type === 'menu' || item.url))
    .slice(0, MAX_BOTTOM_NAV_ITEMS);
  return items.length ? items : DEFAULT_BOTTOM_NAV;
};

/**
 * Alt menünün o anki hâli.
 *
 * Spor ve kategori sayfaları kapalıyken listeden düşer; `settingsLoaded`
 * beklenir, yoksa ayarlar gelene kadar kapalı bölüm bir an görünürdü.
 */
export const useBottomNavItems = () => {
  const settings = useSettings();
  const hiddenCats = useHiddenCategories();
  return useMemo(() => normalizeBottomNav(settings.mobile_bottom_nav).filter(
    (item) => {
      if (item.type !== 'link') return true;
      const path = pathOf(item.url);
      if (path === '/spor') {
        return settings.settingsLoaded && settings.sports_enabled !== false;
      }
      const category = ROUTE_CATEGORIES[path];
      return !category || !hiddenCats.includes(category);
    },
  ), [
    settings.mobile_bottom_nav, settings.settingsLoaded,
    settings.sports_enabled, hiddenCats,
  ]);
};

/** Yuvaların ortadaki sabit düğmeye göre sol/sağ dağılımı. */
export const splitBottomNav = (items) => {
  const half = Math.ceil(items.length / 2);
  return { left: items.slice(0, half), right: items.slice(half) };
};

export const bottomNavIcon = (name) => (knownIcon(name) ? BOTTOM_NAV_ICONS[name] : Home);

export default useBottomNavItems;
